/**
 * FileExplorerVanilla - Vanilla JS File Explorer for standalone terminal mode.
 * Uses inline SVG icons (lucide-style) instead of emoji.
 */
import type { FileEntry, FileError, SortBy, SortOrder, FileExplorerPreferences } from './types';
import { DEFAULT_PREFERENCES } from './types';
import { formatFileSize } from './fileIcons';

const STORAGE_KEY = 'whss-file-explorer-prefs';

type ContextAction = 'copy-path' | 'copy-name' | 'edit-micro' | 'cat' | 'cd' | 'reveal';

export interface FileExplorerVanillaOptions {
  container: HTMLElement;
  onListDirectory: (
    path: string,
    options: { showHidden: boolean; sortBy: SortBy; sortOrder: SortOrder }
  ) => void;
  onPathInsert: (path: string) => void;
  onCommand: (command: string) => void;
  onSidebarToggle?: (collapsed: boolean) => void;
}

// Lucide-style SVG icons (24x24 viewBox, stroke-based)
const ICONS = {
  folder: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z"/></svg>',
  file: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"/><path d="M14 2v4a2 2 0 0 0 2 2h4"/></svg>',
  chevronUp: '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m18 15-6-6-6 6"/></svg>',
  home: '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8"/><path d="M3 10a2 2 0 0 1 .709-1.528l7-5.999a2 2 0 0 1 2.582 0l7 5.999A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/></svg>',
  refreshCw: '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/><path d="M21 3v5h-5"/><path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/><path d="M8 16H3v5"/></svg>',
  panelLeftClose: '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2"/><path d="M9 3v18"/><path d="m16 15-3-3 3-3"/></svg>',
  panelLeftOpen: '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2"/><path d="M9 3v18"/><path d="m14 9 3 3-3 3"/></svg>',
  loader: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2v4"/><path d="m16.2 7.8 2.9-2.9"/><path d="M18 12h4"/><path d="m16.2 16.2 2.9 2.9"/><path d="M12 18v4"/><path d="m4.9 19.1 2.9-2.9"/><path d="M2 12h4"/><path d="m4.9 4.9 2.9 2.9"/></svg>',
  folderOpen: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m6 14 1.5-2.9A2 2 0 0 1 9.24 10H20a2 2 0 0 1 1.94 2.5l-1.54 6a2 2 0 0 1-1.95 1.5H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h3.9a2 2 0 0 1 1.69.9l.81 1.2a2 2 0 0 0 1.67.9H18a2 2 0 0 1 2 2v2"/></svg>',
  link: '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>',
  copy: '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="14" height="14" x="8" y="8" rx="2" ry="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/></svg>',
  pencil: '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z"/></svg>',
  eye: '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0"/><circle cx="12" cy="12" r="3"/></svg>',
  terminal: '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="4 17 10 11 4 5"/><line x1="12" x2="20" y1="19" y2="19"/></svg>',
  folderSearch: '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="17" cy="17" r="3"/><path d="M10.7 20H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h3.9a2 2 0 0 1 1.69.9l.81 1.2a2 2 0 0 0 1.67.9H20a2 2 0 0 1 2 2v4.1"/><path d="m21 21-1.5-1.5"/></svg>',
  alertCircle: '<svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" x2="12" y1="8" y2="12"/><line x1="12" x2="12.01" y1="16" y2="16"/></svg>',
  folderX: '<svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z"/><path d="m9.5 10.5 5 5"/><path d="m14.5 10.5-5 5"/></svg>',
};

function escapeShellPath(path: string): string {
  if (/\s/.test(path)) {
    return `"${path}"`;
  }
  return path;
}

function getParentPath(path: string): string {
  if (path === '/' || path === '~') return path;
  // Windows drive root (e.g., C:\ or C:)
  if (/^[A-Z]:\\?$/i.test(path)) return path;
  // Detect separator style
  const sep = path.includes('\\') ? '\\' : '/';
  const parts = path.split(/[\\/]/);
  parts.pop();
  if (parts.length === 0) return '/';
  // Windows: preserve drive root (e.g., ["C:", ""] -> "C:\")
  if (parts.length === 1 && /^[A-Z]:$/i.test(parts[0]!)) return parts[0] + '\\';
  return parts.join(sep);
}

function formatError(error: FileError | null): string | null {
  if (!error) return null;
  switch (error.type) {
    case 'not_found': return 'Directory not found';
    case 'permission_denied': return 'Permission denied';
    case 'not_a_directory': return 'Not a directory';
    case 'path_traversal': return 'Path traversal blocked';
    case 'outside_allowed_roots': return 'Outside allowed roots';
    case 'invalid_path': return 'Invalid path';
    case 'io_error': return `I/O error: ${error.message}`;
    default: return 'Unknown error';
  }
}


function getEntryIcon(entry: FileEntry): string {
  if (entry.entry_type === 'directory') return ICONS.folder;
  if (entry.symlink) return ICONS.link;
  return ICONS.file;
}

export class FileExplorerVanilla {
  private container: HTMLElement;
  private options: FileExplorerVanillaOptions;
  private prefs: FileExplorerPreferences;
  private currentPath: string = '~';
  private entries: FileEntry[] = [];
  private loading: boolean = false;
  private error: FileError | null = null;
  private terminalCwd: string | null = null;
  private collapsed: boolean = false;
  private selectedEntry: string | null = null;
  private contextMenu: HTMLElement | null = null;

  constructor(options: FileExplorerVanillaOptions) {
    this.container = options.container;
    this.options = options;
    this.prefs = this.loadPreferences();
    this.collapsed = this.prefs.collapsed;
    // Apply collapsed class to container for CSS width
    this.container.classList.toggle('collapsed', this.collapsed);
    this.render();
    this.setupEventListeners();
  }

  private loadPreferences(): FileExplorerPreferences {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) return { ...DEFAULT_PREFERENCES, ...JSON.parse(stored) };
    } catch { /* ignore */ }
    return { ...DEFAULT_PREFERENCES };
  }

  private savePreferences(prefs: Partial<FileExplorerPreferences>): void {
    try {
      const current = this.loadPreferences();
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...current, ...prefs }));
    } catch { /* ignore */ }
  }

  private render(): void {
    if (this.collapsed) {
      this.renderCollapsed();
    } else {
      this.renderExpanded();
    }
  }

  private renderCollapsed(): void {
    this.container.innerHTML = `
      <div class="fe" data-collapsed>
        <button class="fe-expand-btn" title="Open file explorer" aria-label="Open file explorer">
          ${ICONS.panelLeftOpen}
        </button>
      </div>
    `;
  }

  private renderExpanded(): void {
    const errorMsg = formatError(this.error);
    const followActive = this.prefs.followCwd ? ' active' : '';
    const hiddenActive = this.prefs.showHidden ? ' active' : '';

    this.container.innerHTML = `
      <div class="fe" style="width:${this.prefs.sidebarWidth}px" role="complementary" aria-label="File explorer">
        <div class="fe-header">
          <input type="text" class="fe-path-input" value="${this.esc(this.currentPath)}" spellcheck="false" />
          <button class="fe-icon-btn fe-collapse-btn" title="Close file explorer" aria-label="Close file explorer">
            ${ICONS.panelLeftClose}
          </button>
        </div>
        <div class="fe-toolbar">
          <div class="fe-toolbar-left">
            <button class="fe-icon-btn" data-action="up" title="Go up">${ICONS.chevronUp}</button>
            <button class="fe-icon-btn" data-action="home" title="Go home">${ICONS.home}</button>
            <button class="fe-icon-btn" data-action="refresh" title="Refresh">${ICONS.refreshCw}</button>
          </div>
          <div class="fe-toolbar-right">
            <button class="fe-toggle-btn${followActive}" data-pref="followCwd" title="Follow terminal CWD">
              ${ICONS.link} <span>Follow</span>
            </button>
            <button class="fe-toggle-btn${hiddenActive}" data-pref="showHidden" title="Show hidden files">
              ${ICONS.eye} <span>Hidden</span>
            </button>
          </div>
        </div>
        <div class="fe-tree">
          ${this.loading ? `<div class="fe-status"><div class="fe-spinner">${ICONS.loader}</div><span>Loading...</span></div>` : ''}
          ${errorMsg ? `<div class="fe-status fe-error">${ICONS.alertCircle}<span>${this.esc(errorMsg)}</span></div>` : ''}
          ${!this.loading && !errorMsg ? this.renderEntries() : ''}
        </div>
      </div>
    `;
  }

  private renderEntries(): string {
    if (this.entries.length === 0) {
      return `<div class="fe-status">${ICONS.folderX}<span>Empty directory</span></div>`;
    }
    return this.entries.map(entry => {
      const isDir = entry.entry_type === 'directory';
      const sel = this.selectedEntry === entry.name ? ' selected' : '';
      const hidden = entry.hidden ? ' hidden-entry' : '';
      const sizeStr = isDir ? '' : formatFileSize(entry.size);
      return `
        <div class="fe-entry${sel}${hidden}${isDir ? ' dir' : ''}" data-name="${this.esc(entry.name)}" data-is-dir="${isDir}" role="treeitem" tabindex="0">
          <span class="fe-entry-icon">${getEntryIcon(entry)}</span>
          <span class="fe-entry-name">${this.esc(entry.name)}</span>
          ${sizeStr ? `<span class="fe-entry-meta">${sizeStr}</span>` : ''}
        </div>`;
    }).join('');
  }

  private esc(str: string): string {
    const d = document.createElement('div');
    d.textContent = str;
    return d.innerHTML;
  }

  private setupEventListeners(): void {
    this.container.addEventListener('click', this.handleClick.bind(this));
    this.container.addEventListener('dblclick', this.handleDoubleClick.bind(this));
    this.container.addEventListener('contextmenu', this.handleContextMenu.bind(this));
    this.container.addEventListener('keydown', this.handleKeydown.bind(this));
    document.addEventListener('click', () => this.closeContextMenu());
  }

  private handleClick(e: Event): void {
    const target = e.target as HTMLElement;

    if (target.closest('.fe-expand-btn')) { this.toggleCollapsed(); return; }
    if (target.closest('.fe-collapse-btn')) { this.toggleCollapsed(); return; }

    const actionBtn = target.closest('[data-action]') as HTMLElement;
    if (actionBtn) {
      const action = actionBtn.dataset.action;
      if (action === 'up') this.goUp();
      else if (action === 'home') this.goHome();
      else if (action === 'refresh') this.refresh();
      return;
    }

    const toggleBtn = target.closest('.fe-toggle-btn') as HTMLElement;
    if (toggleBtn) {
      const pref = toggleBtn.dataset.pref;
      if (pref === 'followCwd') {
        this.prefs.followCwd = !this.prefs.followCwd;
        this.savePreferences({ followCwd: this.prefs.followCwd });
        if (this.prefs.followCwd && this.terminalCwd && this.terminalCwd !== this.currentPath) {
          this.navigateTo(this.terminalCwd);
        } else {
          this.render();
        }
      } else if (pref === 'showHidden') {
        this.prefs.showHidden = !this.prefs.showHidden;
        this.savePreferences({ showHidden: this.prefs.showHidden });
        this.refresh();
      }
      return;
    }

    const entry = target.closest('.fe-entry') as HTMLElement;
    if (entry) {
      const name = entry.dataset.name;
      if (name) {
        this.selectEntry(name);
        // Single-click on directory navigates into it
        if (entry.dataset.isDir === 'true') {
          this.navigateTo(this.getFullPath(name));
        }
      }
    }
  }

  private handleDoubleClick(e: Event): void {
    const target = e.target as HTMLElement;
    const entry = target.closest('.fe-entry') as HTMLElement;
    if (entry && entry.dataset.isDir === 'true') {
      const name = entry.dataset.name;
      if (name) {
        const fullPath = this.getFullPath(name);
        if (this.prefs.followCwd) {
          this.options.onCommand(`cd ${escapeShellPath(fullPath)}`);
        } else {
          this.navigateTo(fullPath);
        }
      }
    }
  }

  private handleContextMenu(e: Event): void {
    const target = e.target as HTMLElement;
    const entry = target.closest('.fe-entry') as HTMLElement;
    if (entry) {
      e.preventDefault();
      const name = entry.dataset.name;
      const isDir = entry.dataset.isDir === 'true';
      if (name) this.showContextMenu(e as MouseEvent, name, isDir);
    }
  }

  private handleKeydown(e: KeyboardEvent): void {
    const target = e.target as HTMLElement;
    if (target.classList.contains('fe-path-input') && e.key === 'Enter') {
      this.navigateTo((target as HTMLInputElement).value);
    }
  }

  private showContextMenu(e: MouseEvent, name: string, isDir: boolean): void {
    this.closeContextMenu();
    const menu = document.createElement('div');
    menu.className = 'fe-ctx';
    menu.style.left = `${e.clientX}px`;
    menu.style.top = `${e.clientY}px`;

    const items: { icon: string; label: string; action: ContextAction }[] = [
      { icon: ICONS.copy, label: 'Copy Path', action: 'copy-path' },
      { icon: ICONS.copy, label: 'Copy Name', action: 'copy-name' },
    ];
    if (isDir) {
      items.push({ icon: ICONS.terminal, label: 'Open in Terminal', action: 'cd' });
    } else {
      items.push({ icon: ICONS.pencil, label: 'Edit with micro', action: 'edit-micro' });
      items.push({ icon: ICONS.eye, label: 'View (cat)', action: 'cat' });
    }
    items.push({ icon: ICONS.folderSearch, label: 'Reveal in Terminal', action: 'reveal' });

    menu.innerHTML = items.map(i =>
      `<button class="fe-ctx-item" data-action="${i.action}">${i.icon}<span>${i.label}</span></button>`
    ).join('');

    menu.addEventListener('click', (ev) => {
      const item = (ev.target as HTMLElement).closest('.fe-ctx-item') as HTMLElement;
      if (item) {
        this.handleContextAction(name, isDir, item.dataset.action as ContextAction);
        this.closeContextMenu();
      }
    });

    document.body.appendChild(menu);
    this.contextMenu = menu;
  }

  private closeContextMenu(): void {
    if (this.contextMenu) { this.contextMenu.remove(); this.contextMenu = null; }
  }

  private handleContextAction(name: string, _isDir: boolean, action: ContextAction): void {
    const fullPath = this.getFullPath(name);
    switch (action) {
      case 'copy-path': navigator.clipboard.writeText(fullPath).catch(() => this.options.onPathInsert(fullPath)); break;
      case 'copy-name': navigator.clipboard.writeText(name).catch(() => this.options.onPathInsert(name)); break;
      case 'edit-micro': this.options.onCommand(`micro ${escapeShellPath(fullPath)}`); break;
      case 'cat': this.options.onCommand(`cat ${escapeShellPath(fullPath)}`); break;
      case 'cd': this.options.onCommand(`cd ${escapeShellPath(fullPath)}`); break;
      case 'reveal': this.options.onCommand(`cd ${escapeShellPath(this.currentPath)}`); break;
    }
  }

  private getFullPath(name: string): string {
    if (this.currentPath === '/') return `/${name}`;
    const sep = this.currentPath.includes('\\') ? '\\' : '/';
    // Handle trailing separator
    const base = this.currentPath.replace(/[\\/]$/, '');
    return `${base}${sep}${name}`;
  }

  private selectEntry(name: string): void { this.selectedEntry = name; this.render(); }
  private toggleCollapsed(): void {
    this.collapsed = !this.collapsed;
    this.savePreferences({ collapsed: this.collapsed });
    // Toggle collapsed class on the outer container so CSS can resize it
    this.container.classList.toggle('collapsed', this.collapsed);
    this.render();
    this.options.onSidebarToggle?.(this.collapsed);
  }
  private goUp(): void { const p = getParentPath(this.currentPath); if (p !== this.currentPath) this.navigateTo(p); }
  private goHome(): void { this.navigateTo('~'); }
  private refresh(): void { this.requestDirectory(this.currentPath); }
  private navigateTo(path: string): void { this.currentPath = path; this.selectedEntry = null; this.requestDirectory(path); }

  private requestDirectory(path: string): void {
    this.loading = true;
    this.error = null;
    this.render();
    this.options.onListDirectory(path, {
      showHidden: this.prefs.showHidden,
      sortBy: this.prefs.sortBy,
      sortOrder: this.prefs.sortOrder,
    });
  }

  updateContents(path: string, entries: FileEntry[], truncated: boolean, error: FileError | null): void {
    this.currentPath = path;
    this.entries = entries;
    this.loading = false;
    this.error = error;
    if (truncated) console.warn(`Directory truncated: ${path}`);
    this.render();
  }

  updateCwd(path: string): void {
    const prev = this.terminalCwd;
    this.terminalCwd = path;
    if (this.prefs.followCwd && path !== prev && path !== this.currentPath) this.navigateTo(path);
  }

  initialize(): void { this.requestDirectory(this.currentPath); }
  dispose(): void { this.closeContextMenu(); }
}
